import axios from "axios";
import { reportFrontendError } from "../utils/errorMonitoring";
import { getToken } from "../utils/auth";

const browserOrigin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
const baseURL = process.env.REACT_APP_API_URL || browserOrigin;
const userBaseURL = process.env.REACT_APP_USER_API_URL || "http://localhost:5001";
const catalogBaseURL = process.env.REACT_APP_CATALOG_API_URL || "http://localhost:5003";
const chatBaseURL = process.env.REACT_APP_CHAT_API_URL || "http://localhost:5000";

const SERVICE_BASES = {
  gateway: baseURL,
  user: userBaseURL,
  catalog: catalogBaseURL,
  chat: chatBaseURL,
};

function serviceForPath(url = "") {
  const path = String(url || "");
  if (/^\/api\/(materials|pets|rides|hangouts|advice|trades|search|admin|moderation|uploads)/.test(path)) return "catalog";
  if (/^\/api\/(v1\/auth|auth|profiles|v1\/profile|v1\/users)/.test(path) || /^\/(auth|v1\/auth)/.test(path)) return "user";
  if (/^\/api\/(conversations|messages|notifications|chat)/.test(path)) return "chat";
  return null;
}

function withServiceBase(url, service) {
  const base = SERVICE_BASES[service];
  if (!base) return url;
  if (/^https?:\/\//i.test(String(url || ""))) return url;
  return `${base}${url}`;
}

function readCookie(name) {
  const cookies = String(document.cookie || "").split(/;\s*/);
  const entry = cookies.find((part) => part.startsWith(`${encodeURIComponent(name)}=`));
  return entry ? decodeURIComponent(entry.split("=").slice(1).join("=")) : "";
}

export async function ensureCsrfToken(force = false) {
  if (!force && readCookie("csrf_token")) return readCookie("csrf_token");
  try {
    const response = await axios.get(`${userBaseURL}/api/csrf`, { withCredentials: true });
    return response?.data?.csrfToken || readCookie("csrf_token") || "";
  } catch {
    return "";
  }
}

const client = axios.create({
  baseURL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const service = serviceForPath(config.url || "");
  if (service && service !== "gateway") {
    config.baseURL = SERVICE_BASES[service];
  }

  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

client.interceptors.request.use(async (config) => {
  const method = String(config.method || "get").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    let csrfToken = readCookie("csrf_token");
    if (!csrfToken) {
      try { csrfToken = await ensureCsrfToken(); } catch {}
    }
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const method = String(originalRequest?.method || "get").toUpperCase();
    const csrfRejected = error?.response?.status === 403 && /csrf/i.test(String(error?.response?.data?.error || error?.message || ""));

    if (csrfRejected && originalRequest && !originalRequest.__retriedAfterCsrf && !["GET", "HEAD", "OPTIONS"].includes(method)) {
      originalRequest.__retriedAfterCsrf = true;
      try {
        const csrfToken = await ensureCsrfToken(true);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["X-CSRF-Token"] = csrfToken;
        return client(originalRequest);
      } catch {}
    }

    const isNetworkFailure = !error?.response && String(error?.code || error?.message || "").toLowerCase().includes('network');
    if (isNetworkFailure && originalRequest && !originalRequest.__retriedDirectService) {
      const service = serviceForPath(originalRequest.url || "");
      if (service) {
        originalRequest.__retriedDirectService = true;
        originalRequest.baseURL = "";
        originalRequest.url = withServiceBase(originalRequest.url, service);
        return axios(originalRequest);
      }
    }

    reportFrontendError({
      message: error?.message || 'API request failed',
      stack: error?.stack || '',
      source: 'axios',
      level: error?.response?.status >= 500 ? 'error' : 'warning',
      context: {
        url: originalRequest?.url || '',
        method,
        status: error?.response?.status || null,
        service: serviceForPath(originalRequest?.url || '') || 'gateway',
      },
    });

    if (error?.response?.status === 401) {
      localStorage.removeItem("app_user");
      localStorage.removeItem("app_token");
      window.dispatchEvent(new Event("auth-changed"));
      const protectedPath = /^\/(profile|messages|dashboard|my-listings|admin)/.test(window.location.pathname || "");
      if (protectedPath) {
        const target = encodeURIComponent(window.location.pathname + (window.location.search || ""));
        window.location.assign(`/login?from=${target}`);
      }
    }
    return Promise.reject(error);
  }
);

export default client;
